import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  FileText, 
  CheckCircle, 
  AlertCircle,
  Loader2,
  Trash2,
  Save,
  FileUp
} from 'lucide-react';
import { useExpenses } from '../contexts/ExpenseContext';
import { TravelSubcategory } from '../types';
import * as pdfjsLib from 'pdfjs-dist';

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

interface ParsedTravel {
  id: string;
  date: string;
  subcategory: TravelSubcategory;
  description: string;
  amount: number;
  provider: string;
  selected: boolean;
}

export default function ImportTravel() {
  const { addExpense, expenses } = useExpenses();
  const [pdfText, setPdfText] = useState('');
  const [parsedTravel, setParsedTravel] = useState<ParsedTravel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [importStatus, setImportStatus] = useState<{ success: number; errors: number; skipped: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Check for duplicates
  const isDuplicate = useCallback((date: string, amount: number): boolean => {
    return expenses.some(exp => 
      exp.date === date && 
      exp.category === 'travel' &&
      Math.abs(exp.amount! - amount) < 0.01
    );
  }, [expenses]);

  // Parse Uber receipts
  const parseUber = (section: string): ParsedTravel | null => {
    try {
      if (!section.includes('Uber') && !section.includes('uber.com')) return null;
      
      // Extract date
      const dateMatch = section.match(/(\d{1,2}\/\d{1,2}\/\d{2,4})/i) ||
                       section.match(/(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{1,2}),\s+(\d{4})/i);
      if (!dateMatch) return null;
      
      let formattedDate = '';
      if (dateMatch[0].includes('/')) {
        const parts = dateMatch[1].split('/');
        const year = parts[2].length === 2 ? '20' + parts[2] : parts[2];
        formattedDate = `${year}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
      }
      
      // Extract total
      const totalMatch = section.match(/Total[\s$]*\$?\s*([\d,]+\.?\d{2})/i);
      if (!totalMatch) return null;
      const total = parseFloat(totalMatch[1].replace(/,/g, ''));
      
      // Extract route
      const pickupMatch = section.match(/Pickup[:\s]*([\d:]+\s*[AP]M)?[:\s]*([^\n]+)/i);
      const dropoffMatch = section.match(/Drop[- ]off[:\s]*([\d:]+\s*[AP]M)?[:\s]*([^\n]+)/i);
      const pickup = pickupMatch ? pickupMatch[2].trim().split('\n')[0] : '';
      const dropoff = dropoffMatch ? dropoffMatch[2].trim().split('\n')[0] : '';
      
      // Extract driver name
      const driverMatch = section.match(/(?:You rode with|Thanks for riding with)\s+([A-Z][a-z]+)/i);
      const driver = driverMatch ? driverMatch[1] : 'Driver';
      
      return {
        id: crypto.randomUUID(),
        date: formattedDate,
        subcategory: 'uber',
        description: `Uber - ${driver}${pickup && dropoff ? ` - ${pickup} to ${dropoff}` : ''}`,
        amount: total,
        provider: 'Uber',
        selected: true
      };
    } catch (e) {
      console.error('Uber parse error:', e);
      return null;
    }
  };

  // Parse Lyft receipts
  const parseLyft = (section: string): ParsedTravel | null => {
    try {
      if (!section.includes('Lyft') && !section.includes('lyft')) return null;
      
      // Extract date
      const dateMatch = section.match(/(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{1,2}),\s+(\d{4})/i) ||
                       section.match(/(\d{1,2}\/\d{1,2}\/\d{2,4})/);
      if (!dateMatch) return null;
      
      let formattedDate = '';
      if (dateMatch[0].includes('/')) {
        const parts = dateMatch[1].split('/');
        const year = parts[2].length === 2 ? '20' + parts[2] : parts[2];
        formattedDate = `${year}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
      } else {
        const months: Record<string, string> = {
          'January': '01', 'February': '02', 'March': '03', 'April': '04',
          'May': '05', 'June': '06', 'July': '07', 'August': '08',
          'September': '09', 'October': '10', 'November': '11', 'December': '12'
        };
        formattedDate = `${dateMatch[3]}-${months[dateMatch[1]]}-${dateMatch[2].padStart(2, '0')}`;
      }
      
      // Extract total (look for Pay section with amount)
      const totalMatch = section.match(/Pay[\s\w()]*\$?([\d,]+\.?\d{2})/i);
      if (!totalMatch) return null;
      const total = parseFloat(totalMatch[1].replace(/,/g, ''));
      
      // Extract route
      const pickupMatch = section.match(/Pickup[:\s]*([^\n]+)/i);
      const dropoffMatch = section.match(/Drop[- ]off[:\s]*[\d:]+\s*[AP]M[:\s]*([^\n]+)/i);
      const pickup = pickupMatch ? pickupMatch[1].trim().split('\n')[0] : '';
      const dropoff = dropoffMatch ? dropoffMatch[1].trim().split('\n')[0] : '';
      
      // Extract driver name
      const driverMatch = section.match(/Thanks for riding with\s+([A-Z][a-z]+)/i);
      const driver = driverMatch ? driverMatch[1] : 'Driver';
      
      return {
        id: crypto.randomUUID(),
        date: formattedDate,
        subcategory: 'lyft',
        description: `Lyft - ${driver}${pickup && dropoff ? ` - ${pickup} to ${dropoff}` : ''}`,
        amount: total,
        provider: 'Lyft',
        selected: true
      };
    } catch (e) {
      console.error('Lyft parse error:', e);
      return null;
    }
  };

  // Parse car rental receipts (Budget, National, etc.)
  const parseCarRental = (section: string): ParsedTravel | null => {
    try {
      const isBudget = section.includes('Budget') || section.includes('BUDGET');
      const isNational = section.includes('National') || section.includes('NATIONAL');
      
      if (!isBudget && !isNational) return null;
      
      const provider = isBudget ? 'Budget' : 'National';
      
      // Extract date
      const dateMatch = section.match(/(?:Pickup Date|Start Charges)[:\s]*[☐]?\s*\w+,\s+(\w+)\s+(\d{1,2}),\s+(\d{4})/i) ||
                       section.match(/(\w{3})\s+(\d{1,2}),\s*(\d{4})/);
      if (!dateMatch) return null;
      
      let formattedDate = '';
      const months: Record<string, string> = {
        'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04', 'May': '05', 'Jun': '06',
        'Jul': '07', 'Aug': '08', 'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12',
        'January': '01', 'February': '02', 'March': '03', 'April': '04',
        'June': '06', 'July': '07', 'August': '08', 'September': '09',
        'October': '10', 'November': '11', 'December': '12'
      };
      
      if (dateMatch[1] && dateMatch[2] && dateMatch[3]) {
        const monthKey = dateMatch[1].substring(0, 3);
        const month = months[monthKey] || months[dateMatch[1]];
        formattedDate = `${dateMatch[3]}-${month}-${dateMatch[2].padStart(2, '0')}`;
      }
      
      // Extract total
      const totalMatch = section.match(/(?:Total|Net Charges)[:\s]*(?:USD)?\s*\$?\s*([\d,]+\.?\d{2})/i) ||
                        section.match(/Your Total Due[:\s]*\$?\s*([\d,]+\.?\d{2})/i);
      if (!totalMatch) return null;
      const total = parseFloat(totalMatch[1].replace(/,/g, ''));
      if (total === 0) return null; // Already paid
      
      // Extract locations
      const pickupMatch = section.match(/(?:Pickup Location|Pickup)[:\s]*([^\n]+(?:\n[^\n]+){0,3})/i);
      const returnMatch = section.match(/(?:Return)[:\s]*([^\n]+)/i);
      
      let locations = '';
      if (pickupMatch && returnMatch) {
        const pickup = pickupMatch[1].replace(/\n/g, ' ').trim().split(/\d{3}-\d{3}-\d{4}/)[0].trim();
        const returnLoc = returnMatch[1].replace(/\n/g, ' ').trim().split(/\d{3}-\d{3}-\d{4}/)[0].trim();
        locations = ` - ${pickup} to ${returnLoc}`;
      } else if (pickupMatch) {
        const pickup = pickupMatch[1].replace(/\n/g, ' ').trim().split(/\d{3}-\d{3}-\d{4}/)[0].trim();
        locations = ` - ${pickup}`;
      }
      
      return {
        id: crypto.randomUUID(),
        date: formattedDate,
        subcategory: 'rental',
        description: `${provider} Car Rental${locations}`,
        amount: total,
        provider,
        selected: true
      };
    } catch (e) {
      console.error('Car rental parse error:', e);
      return null;
    }
  };

  // Parse flight receipts (Priceline, airlines, etc.)
  const parseFlight = (section: string): ParsedTravel | null => {
    try {
      const isPriceline = section.includes('Priceline') || section.includes('priceline');
      const hasFlightKeywords = section.includes('flight') || section.includes('Flight') || 
                                section.includes('Departure') || section.includes('airline');
      
      if (!isPriceline && !hasFlightKeywords) return null;
      
      // Extract date
      const dateMatch = section.match(/(\w{3})\s+(\d{1,2})[,\s]+(\d{4})/i) ||
                       section.match(/(\w+)\s+(\d{1,2}),\s+(\d{4})/i);
      if (!dateMatch) return null;
      
      const months: Record<string, string> = {
        'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04', 'May': '05', 'Jun': '06',
        'Jul': '07', 'Aug': '08', 'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12',
        'January': '01', 'February': '02', 'March': '03', 'April': '04',
        'June': '06', 'July': '07', 'August': '08', 'September': '09',
        'October': '10', 'November': '11', 'December': '12'
      };
      
      const monthKey = dateMatch[1].substring(0, 3);
      const month = months[monthKey] || months[dateMatch[1]];
      const formattedDate = `${dateMatch[3]}-${month}-${dateMatch[2].padStart(2, '0')}`;
      
      // Extract total
      const totalMatch = section.match(/(?:Total Charged|Total|Flight subtotal)[:\s]*\$?\s*([\d,]+\.?\d{2})/i);
      if (!totalMatch) return null;
      const total = parseFloat(totalMatch[1].replace(/,/g, ''));
      
      // Extract route
      const routeMatch = section.match(/([A-Z]{3})\s*[-–]\s*([A-Z]{3})/);
      const route = routeMatch ? `${routeMatch[1]} to ${routeMatch[2]}` : '';
      
      // Extract airline
      const airlineMatch = section.match(/(American Airlines|United|Delta|Southwest|Spirit|Frontier|JetBlue)/i);
      const airline = airlineMatch ? airlineMatch[1] : 'Flight';
      
      return {
        id: crypto.randomUUID(),
        date: formattedDate,
        subcategory: 'airfare',
        description: `${airline}${route ? ' - ' + route : ''}`,
        amount: total,
        provider: airline,
        selected: true
      };
    } catch (e) {
      console.error('Flight parse error:', e);
      return null;
    }
  };

  // Parse payment app transactions (Venmo, Zelle, etc.) for shuttle tips
  const parsePaymentApp = (section: string): ParsedTravel | null => {
    try {
      const hasPaymentKeywords = section.includes('Transaction details') || 
                                 section.includes('Payment sent') ||
                                 section.includes('CAPITAL ONE');
      
      if (!hasPaymentKeywords) return null;
      
      // Look for shuttle/tip keywords
      const isShuttle = section.match(/shuttle|tip|transport/i);
      if (!isShuttle) return null;
      
      // Extract amount (usually prominently displayed)
      const amountMatch = section.match(/\$\s*([\d,]+\.?\d{2})/);
      if (!amountMatch) return null;
      const amount = parseFloat(amountMatch[1].replace(/,/g, ''));
      
      // Extract date (various formats)
      const dateMatch = section.match(/(\d{1,2}\/\d{1,2}\/\d{2,4})/) ||
                       section.match(/(Yesterday|Today)/i);
      
      let formattedDate = '';
      if (dateMatch) {
        if (dateMatch[1] === 'Yesterday') {
          const yesterday = new Date();
          yesterday.setDate(yesterday.getDate() - 1);
          formattedDate = yesterday.toISOString().split('T')[0];
        } else if (dateMatch[1] === 'Today') {
          formattedDate = new Date().toISOString().split('T')[0];
        } else {
          const parts = dateMatch[1].split('/');
          const year = parts[2].length === 2 ? '20' + parts[2] : parts[2];
          formattedDate = `${year}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
        }
      }
      
      // Extract recipient
      const recipientMatch = section.match(/(?:To:|For)\s*([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/i);
      const recipient = recipientMatch ? recipientMatch[1] : '';
      
      // Extract description/note
      const noteMatch = section.match(/For\s+([^\n$]+)/i);
      const note = noteMatch ? noteMatch[1].trim() : 'Shuttle/Transport';
      
      return {
        id: crypto.randomUUID(),
        date: formattedDate,
        subcategory: 'other',
        description: `${note}${recipient ? ' - ' + recipient : ''}`,
        amount,
        provider: 'Payment',
        selected: true
      };
    } catch (e) {
      console.error('Payment app parse error:', e);
      return null;
    }
  };

  // Master parse function
  const parseTravelText = (text: string) => {
    const travelExpenses: ParsedTravel[] = [];
    
    // Split by receipt boundaries
    const sections = text.split(/(?=Uber|Lyft|Budget|National|Priceline|Transaction details)/i);
    
    for (const section of sections) {
      if (!section.trim()) continue;
      
      // Try each parser
      let parsed: ParsedTravel | null = null;
      
      parsed = parseUber(section);
      if (!parsed) parsed = parseLyft(section);
      if (!parsed) parsed = parseCarRental(section);
      if (!parsed) parsed = parseFlight(section);
      if (!parsed) parsed = parsePaymentApp(section);
      
      if (parsed && parsed.amount > 0) {
        // Check for duplicates
        const exists = travelExpenses.some(t => 
          t.date === parsed!.date && Math.abs(t.amount - parsed!.amount) < 0.01
        );
        if (!exists) {
          travelExpenses.push(parsed);
        }
      }
    }
    
    return travelExpenses;
  };

  // Extract text from PDF
  const extractTextFromPDF = async (file: File): Promise<string> => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    
    let fullText = '';
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items
        .map((item: any) => item.str)
        .join(' ');
      fullText += pageText + '\n\n';
    }
    
    return fullText;
  };

  // Handle file drop
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;
    
    setIsLoading(true);
    setError(null);
    setImportStatus(null);
    
    try {
      const text = await extractTextFromPDF(file);
      setPdfText(text);
      
      const travel = parseTravelText(text);
      if (travel.length === 0) {
        setError('No travel expenses found in PDF. Try pasting the text manually below.');
      } else {
        setParsedTravel(travel);
      }
    } catch (e: any) {
      console.error('PDF extraction error:', e);
      setError(`Failed to read PDF: ${e.message}. Try pasting the text manually.`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1
  });

  // Parse from pasted text
  const handleParseText = () => {
    if (!pdfText.trim()) return;
    
    setError(null);
    const travel = parseTravelText(pdfText);
    if (travel.length === 0) {
      setError('No travel expenses found in the text. Make sure you copied the full receipt content.');
    } else {
      setParsedTravel(travel);
    }
  };

  // Toggle selection
  const toggleSelection = (id: string) => {
    setParsedTravel(prev => 
      prev.map(t => t.id === id ? { ...t, selected: !t.selected } : t)
    );
  };

  // Remove travel expense
  const removeTravel = (id: string) => {
    setParsedTravel(prev => prev.filter(t => t.id !== id));
  };

  // Get provider badge color
  const getProviderColor = (provider: string) => {
    switch (provider.toLowerCase()) {
      case 'uber': return 'bg-black/60 text-white border-white/30';
      case 'lyft': return 'bg-pink-500/20 text-pink-400 border-pink-500/50';
      case 'budget': return 'bg-orange-500/20 text-orange-400 border-orange-500/50';
      case 'national': return 'bg-green-500/20 text-green-400 border-green-500/50';
      default: return 'bg-blue-500/20 text-blue-400 border-blue-500/50';
    }
  };

  // Import selected travel expenses
  const handleImport = async () => {
    const selected = parsedTravel.filter(t => t.selected);
    if (selected.length === 0) return;
    
    setIsLoading(true);
    let success = 0;
    let errors = 0;
    let skipped = 0;
    
    for (const travel of selected) {
      // Check for duplicates
      if (isDuplicate(travel.date, travel.amount)) {
        skipped++;
        continue;
      }
      
      try {
        await addExpense({
          category: 'travel',
          subcategory: travel.subcategory,
          description: travel.description,
          amount: travel.amount,
          date: travel.date,
          file: new Blob([`${travel.provider} - ${travel.description}`], { type: 'text/plain' }),
          filename: `travel-${travel.provider.toLowerCase()}-${travel.date}.txt`
        });
        success++;
      } catch (e) {
        console.error('Failed to import travel:', e);
        errors++;
      }
    }
    
    setImportStatus({ success, errors, skipped });
    if (success > 0 || skipped > 0) {
      const importedOrSkipped = selected.map(t => t.id);
      setParsedTravel(prev => prev.filter(t => !importedOrSkipped.includes(t.id)));
    }
    setIsLoading(false);
  };

  const selectedCount = parsedTravel.filter(t => t.selected && !isDuplicate(t.date, t.amount)).length;
  const totalAmount = parsedTravel
    .filter(t => t.selected && !isDuplicate(t.date, t.amount))
    .reduce((sum, t) => sum + t.amount, 0);
  const duplicateCount = parsedTravel.filter(t => isDuplicate(t.date, t.amount)).length;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Import Travel Expenses</h1>
        <p className="text-shuttle-sky mt-1">
          Upload receipts from Uber, Lyft, car rentals, flights, and more
        </p>
      </div>

      {/* Supported Providers */}
      <div className="flex flex-wrap gap-2">
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-black/60 text-white border border-white/30">
          ✓ Uber
        </span>
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-pink-500/20 text-pink-400 border border-pink-500/50">
          ✓ Lyft
        </span>
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-orange-500/20 text-orange-400 border border-orange-500/50">
          ✓ Budget
        </span>
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/50">
          ✓ National
        </span>
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/50">
          ✓ Priceline/Airlines
        </span>
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400 border border-purple-500/50">
          ✓ Payment Apps
        </span>
      </div>

      {/* Upload Zone */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all ${
          isDragActive 
            ? 'border-shuttle-sand bg-shuttle-sand/10' 
            : 'border-white/30 hover:border-white/50 bg-white/5'
        }`}
      >
        <input {...getInputProps()} />
        <FileUp className="w-12 h-12 mx-auto mb-4 text-shuttle-sky" />
        {isDragActive ? (
          <p className="text-white text-lg">Drop the PDF here...</p>
        ) : (
          <>
            <p className="text-white text-lg mb-2">
              Drag & drop your travel receipts PDF here
            </p>
            <p className="text-white/60 text-sm">or click to browse</p>
          </>
        )}
      </div>

      {/* Manual Text Input */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
        <h3 className="text-lg font-medium text-white mb-3 flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Or Paste Receipt Text
        </h3>
        <textarea
          value={pdfText}
          onChange={(e) => setPdfText(e.target.value)}
          placeholder="Paste your travel receipts from Uber, Lyft, rental car companies, or airlines..."
          className="w-full h-32 bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-shuttle-sand resize-none font-mono text-sm"
        />
        <button
          onClick={handleParseText}
          disabled={!pdfText.trim()}
          className="mt-3 px-6 py-2 bg-shuttle-sky/20 text-shuttle-sky rounded-lg hover:bg-shuttle-sky/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Parse Text
        </button>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-shuttle-sand" />
          <span className="ml-3 text-white">Processing...</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-red-200">{error}</p>
        </div>
      )}

      {/* Import Status */}
      {importStatus && (
        <div className={`${importStatus.success > 0 ? 'bg-green-500/20 border-green-500/50' : 'bg-yellow-500/20 border-yellow-500/50'} border rounded-xl p-4 flex items-start gap-3`}>
          <CheckCircle className={`w-5 h-5 ${importStatus.success > 0 ? 'text-green-400' : 'text-yellow-400'} flex-shrink-0 mt-0.5`} />
          <div>
            <p className={importStatus.success > 0 ? 'text-green-200' : 'text-yellow-200'}>
              {importStatus.success > 0 && `Successfully imported ${importStatus.success} travel expense(s)!`}
              {importStatus.errors > 0 && ` ${importStatus.errors} failed.`}
            </p>
            {importStatus.skipped > 0 && (
              <p className="text-yellow-300 text-sm mt-1">
                ⚠️ {importStatus.skipped} duplicate(s) skipped - already in database!
              </p>
            )}
          </div>
        </div>
      )}

      {/* Parsed Travel Expenses */}
      {parsedTravel.length > 0 && (
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 overflow-hidden">
          <div className="p-4 border-b border-white/10 flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="text-lg font-medium text-white">
                Found {parsedTravel.length} Travel Expense(s)
              </h3>
              {duplicateCount > 0 && (
                <p className="text-yellow-400 text-sm">
                  ⚠️ {duplicateCount} already in database
                </p>
              )}
            </div>
            <div className="flex items-center gap-4">
              <span className="text-shuttle-sky">
                {selectedCount} new • ${totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
              <button
                onClick={handleImport}
                disabled={selectedCount === 0 || isLoading}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-shuttle-sand to-amber-500 text-shuttle-dark rounded-lg font-medium hover:from-amber-400 hover:to-amber-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <Save className="w-4 h-4" />
                Import Selected
              </button>
            </div>
          </div>

          <div className="divide-y divide-white/10">
            {parsedTravel.map((travel) => {
              const isExisting = isDuplicate(travel.date, travel.amount);
              return (
              <div
                key={travel.id}
                className={`p-4 flex items-center gap-4 transition-colors ${
                  isExisting ? 'bg-yellow-500/10 opacity-60' : travel.selected ? 'bg-white/5' : 'bg-transparent opacity-50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={travel.selected && !isExisting}
                  onChange={() => !isExisting && toggleSelection(travel.id)}
                  disabled={isExisting}
                  className={`w-5 h-5 rounded border-white/30 bg-white/10 text-shuttle-sand focus:ring-shuttle-sand ${isExisting ? 'cursor-not-allowed opacity-50' : ''}`}
                />
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-2xl">{isExisting ? '⚠️' : (travel.subcategory === 'uber' || travel.subcategory === 'lyft') ? '🚗' : travel.subcategory === 'rental' ? '🚙' : travel.subcategory === 'airfare' ? '✈️' : '💰'}</span>
                    {isExisting && (
                      <span className="px-2 py-0.5 rounded text-xs font-medium border bg-yellow-500/20 text-yellow-400 border-yellow-500/50">
                        DUPLICATE
                      </span>
                    )}
                    <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getProviderColor(travel.provider)}`}>
                      {travel.provider}
                    </span>
                    <span className="text-white/50">•</span>
                    <span className="text-white/70 capitalize text-sm">
                      {travel.subcategory}
                    </span>
                    <span className="text-white/50">•</span>
                    <span className="text-white/70 text-sm">
                      {travel.date}
                    </span>
                  </div>
                  <p className="text-white/80 text-sm truncate">
                    {travel.description}
                  </p>
                </div>

                <div className="text-right">
                  <p className="text-xl font-bold text-red-400">
                    -${travel.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </p>
                </div>

                <button
                  onClick={() => removeTravel(travel.id)}
                  className="p-2 text-white/40 hover:text-red-400 transition-colors"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            );
            })}
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
        <h3 className="text-lg font-medium text-white mb-3">🚗 Supported Travel Formats</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-black/30 rounded-lg p-4 border border-white/20">
            <h4 className="font-medium text-white mb-2">🚗 Ride Shares</h4>
            <ul className="text-sm text-white/70 space-y-1">
              <li>• Uber receipts (email)</li>
              <li>• Lyft receipts (email)</li>
              <li>• Driver, route, & totals</li>
            </ul>
          </div>
          <div className="bg-orange-500/10 rounded-lg p-4 border border-orange-500/30">
            <h4 className="font-medium text-orange-400 mb-2">🚙 Car Rentals</h4>
            <ul className="text-sm text-white/70 space-y-1">
              <li>• Budget rental agreements</li>
              <li>• National rental agreements</li>
              <li>• Pickup/return locations</li>
            </ul>
          </div>
          <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-500/30">
            <h4 className="font-medium text-blue-400 mb-2">✈️ Flights & Other</h4>
            <ul className="text-sm text-white/70 space-y-1">
              <li>• Priceline bookings</li>
              <li>• Airline receipts</li>
              <li>• Shuttle payments (Venmo/Zelle)</li>
            </ul>
          </div>
        </div>
        <p className="mt-4 text-white/50 text-xs">
          💡 Paste or upload your receipts - the parser auto-detects the format!
        </p>
      </div>
    </div>
  );
}

